<template>
  <div>
    <main-layout>
      <AdminMenu />
      <v-container>
        <v-layout wrap class="pt-4">
          <v-flex xs12>
            <v-breadcrumbs :items="items">
              <template v-slot:item="props">
                <router-link
                  :to="{ name: 'patient-history' }"
                  :class="[props.item.disabled && 'disabled']"
                  >{{ props.item.text.toUpperCase() }}</router-link
                >
              </template>
            </v-breadcrumbs>
          </v-flex>
          <v-flex xs12 v-if="$route.params.id" class="py-3">
            <ProfileCard :memberId="$route.params.id" />
          </v-flex>
          <v-flex xs12 sm6 v-if="$route.params.id" class="py-3">
            <PatientChart />
          </v-flex>
          <v-flex xs12 sm6 v-if="$route.params.id" class="py-3">
            <PatientSummaryCard />
          </v-flex>
          <v-flex xs12 sm6>
            <v-layout wrap class="form-control">
              <v-flex xs6 px-2>
                <div style="display: flex">
                  <DatepickerCustomDialog
                    v-model="filter.date_from"
                    :allowed-dates="allowedDates"
                    @input="handleChangeDate"
                  >
                    <template v-slot="{ computedDate }">
                      <input
                        type="text"
                        :value="computedDate"
                        placeholder="วันที่เริ่มต้น"
                        readonly
                      />
                    </template>
                  </DatepickerCustomDialog>
                  <v-btn
                    icon
                    ripple
                    @click="
                      filter.date_from = ''
                      reset()
                    "
                  >
                    <v-icon>close</v-icon>
                  </v-btn>
                </div>
              </v-flex>
              <v-flex xs6 px-2>
                <div style="display: flex">
                  <DatepickerCustomDialog
                    v-model="filter.date_to"
                    :allowed-dates="allowedDates"
                    @input="handleChangeDate"
                  >
                    <template v-slot="{ computedDate }">
                      <input
                        type="text"
                        :value="computedDate"
                        placeholder="วันที่สิ้นสุด"
                        readonly
                      />
                    </template>
                  </DatepickerCustomDialog>
                  <v-btn
                    icon
                    ripple
                    @click="
                      filter.date_to = ''
                      reset()
                    "
                  >
                    <v-icon>close</v-icon>
                  </v-btn>
                </div>
              </v-flex>
            </v-layout>
          </v-flex>
          <v-flex xs12 sm6 class="text-sm-right text-xs-right">
            <v-btn dark outline color="primary" @click="handleExportXlsx" :loading="loading">
              <em class="fas fa-file-export" />&nbsp;{{ $t('export') }}
            </v-btn>
          </v-flex>
        </v-layout>
        <v-data-table
          hide-actions
          :loading="loading"
          :headers="headers"
          :items="patientListData"
          :pagination.sync="pagination"
          class="elevation-1"
        >
          <template slot="headerCell" slot-scope="props">
            <HeaderTableWithSort
              :headers="props.header"
              :sortBy.sync="sortBy"
              :orderBy.sync="orderBy"
              @onSort="handleChangeSort"
            ></HeaderTableWithSort>
          </template>
          <template v-slot:items="props">
            <tr>
              <td>{{ handleGetDateFormatted(props.item.date) }}</td>
              <td>{{ props.item.drink || '-' }}</td>
              <td>{{ props.item.alcohol || '-' }}</td>
              <td>{{ props.item.container || '-' }}</td>
              <td>{{ props.item.quantity || '-' }}</td>
              <td>{{ props.item.standard_drink || '-' }}</td>
              <td><span v-html="handleGetStatusColor(props.item.status)"></span></td>
            </tr>
          </template>
        </v-data-table>
        <div class="mt-2">
          <v-pagination
            :total-visible="6"
            v-model="pageNumber"
            @input="changePage"
            :length="pagelength"
          ></v-pagination>
        </div>
      </v-container>
    </main-layout>
  </div>
</template>

<script>
import nongtungjaiApi from '@/api/nongtungjai'
import { mapGetters } from 'vuex'
import XLSX from 'xlsx'
import moment from 'moment'
import HeaderTableWithSort from '@/components/elements/HeaderTableWithSort.vue'
import { ORDER_BY } from '@/constants/orderBy.js'
import Multiselect from 'vue-multiselect'
import DatepickerCustomDialog from '@/components/elements/DatepickerCustomDialog.vue'
import AdminMenu from './AdminMenu.vue'
import ProfileCard from './ProfileCard.vue'
import PatientChart from './PatientChart.vue'
import PatientSummaryCard from './PatientSummaryCard.vue'
import { DATETIME } from '@/constants/datetime.js'

export default {
  components: {
    HeaderTableWithSort,
    Multiselect,
    DatepickerCustomDialog,
    AdminMenu,
    ProfileCard,
    PatientSummaryCard,
    PatientChart
  },
  data() {
    return {
      limit: 10,
      offset: 0,
      pagination: { rowsPerPage: -1, sortBy: '' },
      total: 0,
      pagetmp: 1,
      pageNumber: 1,
      loading: false,
      patientListData: [],
      headers: [
        { text: 'วันที่', value: 'date', customSort: true, sortable: false },
        { text: 'เครื่องดื่ม', value: 'drink', customSort: true, sortable: false },
        { text: 'ค่าความเข้มข้น', value: 'alcohol', customSort: false, sortable: false },
        { text: 'ภาชนะ', value: 'container', customSort: true, sortable: false },
        { text: 'ปริมาณ', value: 'quantity', customSort: true, sortable: false },
        { text: 'Summary', value: 'standard_drink', customSort: false, sortable: false },
        { text: 'สถานะ', value: 'status', customSort: true, sortable: false }
      ],
      sortBy: 'id',
      orderBy: ORDER_BY.DESC,
      showAdvancedSearch: false,
      statusOptions: ['ความเสี่ยงต่ำ', 'ความเสี่ยงปานกลาง', 'ความเสี่ยงสูง'],
      genderOptions: [
        {
          label: 'ชาย',
          value: 'male'
        },
        {
          label: 'หญิง',
          value: 'female'
        }
      ],
      filter: {
        first_name: '',
        last_name: '',
        age: '',
        gender: '',
        status: '',
        date_from: '',
        date_to: ''
      },
      items: [
        {
          text: 'Patient history',
          disabled: false,
          href: 'patient-history'
        },
        {
          text: 'View',
          disabled: true,
          href: 'patient-history'
        }
      ]
    }
  },
  computed: {
    ...mapGetters(['selectedBot']),
    pagelength() {
      if (this.total) {
        let total = parseFloat(this.total)
        return Math.ceil(total / this.limit)
      } else {
        return 1
      }
    }
  },
  async mounted() {
    await this.handleGetPatientDetails()
  },
  methods: {
    async handleGetPatientDetails() {
      const vm = this
      try {
        const response = await vm.getPatientDetails()
        if (response) {
          vm.patientListData = response.data
          vm.total = response.total
        }
      } catch (error) {
        console.error(error)
      }
    },
    async getPatientDetails() {
      const vm = this
      let member_id = vm.$route && vm.$route.params.id
      const limit = vm.isExport ? vm.total : vm.limit
      let data = {
        page_id: vm.selectedBot.pageid,
        limit: limit,
        offset: vm.offset,
        sort_by: vm.sortBy,
        order_by: vm.orderBy
      }

      Object.entries(vm.filter).forEach(([key, value]) => {
        if (value && value.length) {
          Object.assign(data, { [key]: value.toLowerCase() })
        }
      })

      vm.loading = true
      const response = await nongtungjaiApi.getPatientDetails(
        {
          config: {
            params: data
          }
        },
        member_id
      )

      vm.loading = false
      if (response && response.status === 200) {
        return response.data
      } else {
        return null
      }
    },
    async handleExportXlsx() {
      const vm = this
      vm.isExport = true
      const data = await vm.getPatientDetails()
      const requestData = data.data
      vm.isExport = false

      if (requestData == null) return
      const requests = requestData.map((el) => {
        return {
          date: vm.handleGetDateFormatted(el.date),
          drink: el.drink ? el.drink : '-',
          alcohol: el.alcohol ? `${el.alcohol}%` : '-',
          container: el.container ? el.container : '-',
          quantity: el.quantity ? el.quantity : '-',
          standard_drink: el.standard_drink ? el.standard_drink : '-',
          status: el.status ? el.status : '-'
        }
      })

      const heading = [
        ['วันที่', 'เครื่องดื่ม', 'ค่าความเข้มข้น', 'ภาชนะ', 'ปริมาณ', 'ดื่มมาตรฐาน', 'สถานะ']
      ]

      const wb = XLSX.utils.book_new()
      const ws = XLSX.utils.json_to_sheet([])
      XLSX.utils.sheet_add_aoa(ws, heading)
      XLSX.utils.sheet_add_json(ws, requests, { origin: 'A2', skipHeader: true })
      XLSX.utils.book_append_sheet(wb, ws)
      XLSX.writeFile(wb, `patient-history-view.xlsx`)
    },
    handleGetDateWithTime(value) {
      try {
        const date = new Date(value)
        return moment(date).format(DATETIME.DATETIME_FORMAT_DISPLAY)
      } catch (error) {
        return ''
      }
    },
    handleGetDateFormatted(value) {
      return value ? moment(value).format(DATETIME.DATE_FORMAT_DISPLAY) : '-'
    },
    handleGetGenderLabel(value) {
      return value === 'male' ? 'ชาย' : 'หญิง'
    },
    handleGetStatusColor(status) {
      if (status) {
        let color = ''
        switch (status) {
          case 'ความเสี่ยงต่ำ':
            color = 'success'
            break
          case 'ความเสี่ยงปานกลาง':
            color = 'yellow'
            break
          case 'ความเสี่ยงสูง':
            color = 'error'
            break

          default:
            color = 'primary'
            break
        }
        return `<span class="${color}--text">${status}</span>`
      } else {
        return '-'
      }
    },

    async deleteBranch(id) {
      const vm = this
      try {
        let data = {
          page_id: vm.selectedBot.pageid,
          id: id
        }
        const response = await yasaleAcApi.deleteBranch({ data: data })
        if (response && response.status === 200) {
          await this.$swal.fire({
            text: 'สำเร็จ',
            type: 'success'
          })
          vm.handleGetPatientDetails()
        }
      } catch (error) {
        console.error(error)
        return
      }
    },
    handleClearSearch() {
      this.filter = {
        first_name: '',
        last_name: '',
        age: '',
        gender: '',
        status: '',
        date_from: '',
        date_to: ''
      }
      this.reset()
      this.handleCloseSearch()
    },
    handleCloseSearch() {
      this.showAdvancedSearch = false
    },
    changePage(val) {
      const vm = this
      if (vm.pagetmp !== val) {
        vm.pagetmp = val
        vm.offset = vm.pageNumber * vm.limit - vm.limit

        vm.handleGetPatientDetails()
      }
    },
    reset() {
      this.offset = 0
      this.pagetmp = 1
      this.pageNumber = 1
      this.handleGetPatientDetails()
    },
    handleChangeSort() {
      this.reset()
    },
    handleChangeDate() {
      let date_from = this.filter.date_from
      let date_to = this.filter.date_to
      if (date_from && date_to) {
        if (moment(date_to).isBefore(date_from)) {
          this.filter.date_from = date_to
        }
      }
      this.reset()
    },
    allowedDates(val) {
      return moment().diff(val, 'days') > 0
    }
  }
}
</script>

<style lang="scss" scoped>
tr > td {
  white-space: nowrap;
}
.disabled {
  color: grey;
  pointer-events: none;
}
</style>



// WEBPACK FOOTER //
// src/components/view/template/NongTungJai/PatientHistoryDetails.vue